import {
  Armchair,
  ArrowRight,
  ArrowRightLeft,
  Car,
  ChevronDown,
  Clock,
  Hotel,
  Info,
  Luggage,
  Shuffle,
  Ticket,
  TrainFront,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { PnrDisplaySegment, PnrDisplayView } from "../../api/use-claim-pnr-display";
import { TicketStatusBadge } from "../passengers/coupon-history-detail";
import {
  type ActualTime,
  actualTimes,
  airportCity,
  airportName,
  CABIN_LABELS,
  CAR_RENTAL_COMPANIES,
  cancellationNotice,
  couponStatusCodes,
  ELEMENT_TYPE_LABELS,
  EMPTY_REFERENCE,
  EQUIPMENT_LABELS,
  elapsedMinutes,
  formatDateRange,
  formatDuration,
  formatNaiveDate,
  formatNaiveTime,
  GROUND_TYPE_LABELS,
  groupItinerary,
  type Journey,
  type Layover,
  linkReplacements,
  minutesBetween,
  type PnrReference,
  type Replacements,
  reroutingDeltas,
  SEGMENT_STATUS_DETAIL,
  SEGMENT_STATUS_FAMILY,
  SEGMENT_STATUS_FAMILY_LABEL,
  SEGMENT_STATUS_SHORT,
  type SegmentTone,
  type Stay,
  segmentNotes,
  segmentStatusCode,
  segmentStatusTone,
  sequenceItinerary,
} from "./pnr-itinerary-lib";

const PILL_TONE: Record<SegmentTone | "indigo", string> = {
  ok: "border-green-600/15 bg-green-50 text-green-700",
  warn: "border-amber-600/15 bg-amber-50 text-amber-700",
  critical: "border-red-600/15 bg-red-50 text-red-700",
  neutral: "border-gray-200 bg-gray-50 text-gray-700",
  indigo: "border-indigo-600/15 bg-indigo-50 text-indigo-700",
};

const GRID = "grid grid-cols-[minmax(4rem,auto)_1.25rem_minmax(0,1fr)] gap-x-3";

function Pill({
  tone = "neutral",
  className,
  children,
}: {
  tone?: SegmentTone | "indigo";
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        PILL_TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Red pill with the dot: everything the rerouting touched wears it. */
function ReroutingPill({ children }: { children: React.ReactNode }) {
  return (
    <Pill tone="critical">
      <span className="size-1.5 rounded-full bg-red-600" aria-hidden />
      {children}
    </Pill>
  );
}

/** `HK · confirmé` — the code a jurist checks, the wording a newcomer reads, the detail on hover. */
function StatusPill({ code }: { code: string }) {
  const family = SEGMENT_STATUS_FAMILY[code];
  const short =
    SEGMENT_STATUS_SHORT[code] ?? (family ? SEGMENT_STATUS_FAMILY_LABEL[family].label.toLowerCase() : null);

  return (
    <Tooltip>
      <TooltipTrigger render={<Pill tone={segmentStatusTone(code)} />}>
        {short ? `${code} · ${short}` : code}
      </TooltipTrigger>
      <TooltipPopup>{SEGMENT_STATUS_DETAIL[code] ?? `Code Amadeus ${code}`}</TooltipPopup>
    </Tooltip>
  );
}

/** Same badge as the e-ticket history, so « Vol effectué » reads the same everywhere. */
function CouponStatusBadge({ code }: { code: string }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>
        <TicketStatusBadge status={code} statusDescription={code} />
      </TooltipTrigger>
      <TooltipPopup>
        Statut e-ticket <span className="font-mono">{code}</span>
      </TooltipPopup>
    </Tooltip>
  );
}

/** The one « Vol annulé » of the page: it comes from flight monitoring, never from an action code. */
function MonitoringCancelledPill() {
  return (
    <Tooltip>
      <TooltipTrigger render={<Pill tone="critical" />}>Vol annulé</TooltipTrigger>
      <TooltipPopup>Via la surveillance des vols</TooltipPopup>
    </Tooltip>
  );
}

function Chip({ dashed = false, children }: { dashed?: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs [&_svg]:size-3.5 [&_svg]:text-gray-500",
        dashed
          ? "border-dashed border-gray-300 bg-gray-50 text-gray-500"
          : "border-gray-200 bg-white text-gray-700",
      )}
    >
      {children}
    </span>
  );
}

function flightLabel(segment: PnrDisplaySegment) {
  return `${segment.carrier ?? ""} ${segment.flightNumber ?? ""}`.trim();
}

/** « 3 h 50 plus tard » / « 20 min plus tôt », red either way: it is the rerouting's cost. */
function shiftLabel(minutes: number) {
  return `${formatDuration(Math.abs(minutes))} plus ${minutes < 0 ? "tôt" : "tard"}`;
}

function FlightStatusPills({
  segment,
  view,
  cancelledByMonitoring,
  replaces,
}: {
  segment: PnrDisplaySegment;
  view: PnrDisplayView;
  cancelledByMonitoring: boolean;
  replaces: PnrDisplaySegment[];
}) {
  const statusCode = segmentStatusCode(segment);
  const declaredReprotection =
    view.rerouting.detected && view.rerouting.reprotectionSegmentTattoo === segment.tattoo;

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {replaces.map((former) => (
        <ReroutingPill key={former.tattoo ?? former.line}>Remplace {flightLabel(former)}</ReroutingPill>
      ))}
      {replaces.length === 0 && declaredReprotection ? <ReroutingPill>Réacheminement</ReroutingPill> : null}
      {cancelledByMonitoring ? <MonitoringCancelledPill /> : null}
      {segment.bookedBySource === "airline" ? (
        <Tooltip>
          <TooltipTrigger render={<Pill tone="warn" />}>Ajouté par la compagnie</TooltipTrigger>
          <TooltipPopup>
            Segment mis dans le dossier par la compagnie, pas par l'agence
            {segment.bookedByOffice ? (
              <>
                {" "}
                · bureau <span className="font-mono">{segment.bookedByOffice}</span>
              </>
            ) : null}
          </TooltipPopup>
        </Tooltip>
      ) : null}
      {statusCode !== "TK" && segment.statusHistory.some((code) => code === "TK" || code === "TL") ? (
        <Tooltip>
          <TooltipTrigger render={<Pill tone="warn" />}>Horaire modifié</TooltipTrigger>
          <TooltipPopup>
            Historique Amadeus :{" "}
            <span className="font-mono">
              {[...segment.statusHistory, statusCode].filter(Boolean).join(" → ")}
            </span>
          </TooltipPopup>
        </Tooltip>
      ) : null}
      {statusCode ? <StatusPill code={statusCode} /> : null}
      {couponStatusCodes(segment).map((code) => (
        <CouponStatusBadge key={code} code={code} />
      ))}
    </span>
  );
}

/** « réel 13:18 +2 h 48 » — the delay turns amber from 15 min, blue when early, green otherwise. */
function ActualTimeLine({ actual }: { actual: ActualTime }) {
  const delay = actual.delayMinutes;
  const tone =
    delay === null
      ? "text-gray-500"
      : delay >= 15
        ? "text-amber-700"
        : delay < 0
          ? "text-blue-700"
          : "text-green-700";

  return (
    <div className="flex items-baseline gap-1.5 text-sm whitespace-nowrap tabular-nums">
      <span className="text-xs text-gray-500">réel</span>
      <span className="font-semibold text-gray-900">
        {actual.local}
        {actual.isUtc ? " UTC" : ""}
      </span>
      {delay !== null ? (
        <span className={cn("text-xs font-semibold", tone)}>
          {delay === 0 ? "à l'heure" : `${delay > 0 ? "+" : "−"}${formatDuration(Math.abs(delay))}`}
        </span>
      ) : null}
    </div>
  );
}

function AirportRow({
  time,
  actual,
  dayDelta,
  iata,
  terminal,
  filled,
  struck,
  planDeltaMinutes = null,
  reference,
}: {
  time: string | null;
  actual: ActualTime | null;
  dayDelta?: number;
  iata: string | null;
  terminal: string | null;
  filled: boolean;
  struck: boolean;
  /** Arrival against the initial plan, when this flight replaced a dropped one. */
  planDeltaMinutes?: number | null;
  reference: PnrReference;
}) {
  const name = airportName(iata, reference);

  return (
    <>
      <div className="flex flex-col items-start gap-1">
        <div
          className={cn(
            "flex items-baseline gap-1 text-2xl leading-none font-semibold tracking-tight whitespace-nowrap text-gray-950 tabular-nums",
            struck && "text-gray-400 line-through",
          )}
        >
          {formatNaiveTime(time)}
          {dayDelta ? (
            <span className="text-xs font-medium tracking-normal text-amber-700 no-underline">
              +{dayDelta} j
            </span>
          ) : null}
        </div>
        {actual ? <ActualTimeLine actual={actual} /> : null}
      </div>
      <div className="flex justify-center pt-1">
        <span
          className={cn(
            "box-border size-2.5 rounded-full",
            filled ? "bg-primary" : "border-2 border-primary bg-white",
            struck && "opacity-40",
          )}
          aria-hidden
        />
      </div>
      <div className="flex flex-wrap items-baseline gap-2 leading-tight">
        <span
          className={cn(
            "font-mono text-base font-bold text-gray-950",
            struck && "text-gray-400 line-through",
          )}
        >
          {iata ?? "—"}
        </span>
        {name ? <span className={cn("text-sm text-gray-700", struck && "text-gray-400")}>{name}</span> : null}
        {terminal ? (
          <span className="rounded border border-gray-200 px-1.5 text-xs text-gray-500">
            Terminal {terminal}
          </span>
        ) : null}
        {planDeltaMinutes !== null ? (
          <span className="text-xs font-semibold text-red-700">
            {planDeltaMinutes >= 0 ? "+" : "−"}
            {formatDuration(Math.abs(planDeltaMinutes))} vs plan initial
          </span>
        ) : null}
      </div>
    </>
  );
}

function FlightBlock({
  segment,
  view,
  reference,
  replaces,
  showStatuses,
}: {
  segment: PnrDisplaySegment;
  view: PnrDisplayView;
  reference: PnrReference;
  /** The dropped flights this one was booked in place of. */
  replaces: PnrDisplaySegment[];
  showStatuses: boolean;
}) {
  const airline = segment.carrier ? (reference.airlines[segment.carrier] ?? null) : null;
  const airlineName = airline?.name ?? null;
  const equipment = segment.equipment ? (EQUIPMENT_LABELS[segment.equipment] ?? segment.equipment) : null;
  const cabin = segment.cabin ? (CABIN_LABELS[segment.cabin] ?? segment.cabin) : null;
  const duration = elapsedMinutes(
    segment.departureAt,
    segment.from,
    segment.arrivalAt,
    segment.to,
    reference,
  );
  const actual = actualTimes(segment, reference);
  const cancelledByMonitoring = actual?.cancelled === true;
  const passengerName = (tattoo: string | null) =>
    view.passengers.find((passenger) => passenger.tattoo === tattoo)?.raw ?? "Passager";
  const seats = segment.checkIns.filter((checkIn) => checkIn.seat !== null);
  const bags = segment.checkIns.flatMap((checkIn) => (checkIn.checkedBags ? [checkIn.checkedBags] : []));
  const bagCount = bags.reduce((total, bag) => total + bag.count, 0);
  const bagWeight = bags.reduce((total, bag) => total + (bag.weight ?? 0), 0);
  const tickets = view.tickets.flatMap((ticket) =>
    ticket.coupons
      .filter((coupon) => coupon.segment === segment.tattoo)
      .map((coupon) => ({ ticket, coupon })),
  );
  const notes = segmentNotes(segment, view.rawElements);
  const planDelta = replaces[0] ? reroutingDeltas(replaces[0], segment, reference).arrivalMinutes : null;

  return (
    <div className={cn(GRID, "px-4 py-4")}>
      <AirportRow
        time={segment.departureAt}
        actual={cancelledByMonitoring ? null : (actual?.departure ?? null)}
        iata={segment.from}
        terminal={segment.departureTerminal}
        filled={false}
        struck={cancelledByMonitoring}
        reference={reference}
      />

      <div className="pt-3.5 text-xs text-gray-500 tabular-nums">
        {duration !== null ? formatDuration(duration) : ""}
      </div>
      <div className="flex justify-center">
        <span
          className={cn("my-1 min-h-18 w-0.5 bg-gray-300", cancelledByMonitoring && "opacity-50")}
          aria-hidden
        />
      </div>
      <div className="flex flex-col gap-2 py-3">
        <div className="flex flex-wrap items-center gap-2.5">
          {/* The carrier's logo when the reservation knows it; the code on a primary square otherwise. */}
          <Avatar className={cn("size-7 rounded-md", cancelledByMonitoring && "opacity-60")}>
            {airline?.iconUrl ? (
              <AvatarImage src={airline.iconUrl} alt={airlineName ?? segment.carrier ?? ""} />
            ) : null}
            <AvatarFallback className="rounded-md bg-primary text-xs font-bold tracking-wide text-primary-foreground">
              {segment.carrier ?? "—"}
            </AvatarFallback>
          </Avatar>
          <span className={cn("text-sm text-gray-900", cancelledByMonitoring && "text-gray-500")}>
            {airlineName ? (
              <span className={cn("font-semibold", cancelledByMonitoring && "line-through")}>
                {airlineName}{" "}
              </span>
            ) : null}
            <span
              className={cn(
                airlineName ? "text-gray-600" : "font-semibold",
                cancelledByMonitoring && "line-through",
              )}
            >
              {flightLabel(segment)}
            </span>
          </span>
          {equipment ? (
            <>
              <span className="text-gray-400">·</span>
              <span className="text-sm text-gray-600">{equipment}</span>
            </>
          ) : null}
          {cabin ? (
            <>
              <span className="text-gray-400">·</span>
              <span className="text-sm text-gray-600">
                {cabin}
                {segment.bookingClass ? ` · classe ${segment.bookingClass}` : ""}
              </span>
            </>
          ) : null}
          {showStatuses ? (
            <span className="ml-auto">
              <FlightStatusPills
                segment={segment}
                view={view}
                cancelledByMonitoring={cancelledByMonitoring}
                replaces={replaces}
              />
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {seats.length > 0 ? (
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <Chip>
                  <Armchair aria-hidden />
                  {`${seats.length > 1 ? "Sièges" : "Siège"} ${seats
                    .slice(0, 3)
                    .map((checkIn) => checkIn.seat)
                    .join(" / ")}${seats.length > 3 ? " …" : ""}`}
                </Chip>
              </TooltipTrigger>
              <TooltipPopup>
                <ul className="flex flex-col gap-0.5">
                  {seats.map((checkIn) => (
                    <li key={`${checkIn.passengerTattoo}-${checkIn.seat}`}>
                      <span className="font-mono font-semibold">{checkIn.seat}</span> ·{" "}
                      {passengerName(checkIn.passengerTattoo)}
                    </li>
                  ))}
                </ul>
              </TooltipPopup>
            </Tooltip>
          ) : (
            <Chip dashed>Siège non attribué</Chip>
          )}
          {bagCount > 0 ? (
            <Chip>
              <Luggage aria-hidden />
              {bagCount} bagage{bagCount > 1 ? "s" : ""}
              {bagWeight > 0 ? ` · ${bagWeight} kg` : ""}
            </Chip>
          ) : null}
          {tickets.map(({ ticket, coupon }) => (
            <Tooltip key={`${ticket.number}-${coupon.number}`}>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <Chip>
                  <Ticket aria-hidden />
                  Billet {ticket.airlineCode ?? ""}-{ticket.number ?? ""}
                  {coupon.number ? ` · coupon ${coupon.number}` : ""}
                </Chip>
              </TooltipTrigger>
              <TooltipPopup>{passengerName(ticket.passengerTattoo)}</TooltipPopup>
            </Tooltip>
          ))}
        </div>

        {notes.map((note) => (
          <div key={note.raw} className="flex items-start gap-1.5 text-xs text-amber-700">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span title={note.raw}>{note.text}</span>
          </div>
        ))}
      </div>

      <AirportRow
        time={segment.arrivalAt}
        actual={cancelledByMonitoring ? null : (actual?.arrival ?? null)}
        dayDelta={segment.dayDelta}
        iata={segment.to}
        terminal={segment.arrivalTerminal}
        filled
        struck={cancelledByMonitoring}
        planDeltaMinutes={planDelta}
        reference={reference}
      />
    </div>
  );
}

/**
 * The flight the airline dropped, drawn in the same grammar as the others but struck through and
 * red, in its place on the timeline. Not collapsible: it folds with its journey.
 */
function GhostLeg({
  former,
  replacement,
  view,
  reference,
}: {
  former: PnrDisplaySegment;
  replacement: PnrDisplaySegment | null;
  view: PnrDisplayView;
  reference: PnrReference;
}) {
  const airline = former.carrier ? (reference.airlines[former.carrier] ?? null) : null;
  const statusCode = segmentStatusCode(former);
  const notice = cancellationNotice(former, view.rawElements);
  const duration = elapsedMinutes(former.departureAt, former.from, former.arrivalAt, former.to, reference);
  // The reissued coupons sit on the replacement segment: that is the ticket trail of this flight.
  const reissued = replacement
    ? view.tickets.flatMap((ticket) =>
        ticket.coupons
          .filter((coupon) => coupon.segment === replacement.tattoo)
          .map((coupon) => ({ ticket, coupon })),
      )
    : [];
  const strike = "line-through decoration-red-600 decoration-2";

  return (
    <div className={cn(GRID, "bg-gradient-to-r from-red-50/70 to-red-50/25 px-4 py-3")}>
      <div className="col-start-3 mb-2.5 flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold tracking-wider text-red-700 uppercase">Vol initial annulé</span>
        {/* The day matters as much as the hours: EU261 and the rerouting are judged against it. */}
        <span className="text-sm font-medium text-gray-700">
          {formatNaiveDate(former.departureAt, { weekday: true, year: true })}
        </span>
        {statusCode ? <StatusPill code={statusCode} /> : null}
        {notice ? (
          <span className="text-xs text-gray-500">
            {notice.source} notifié le {formatNaiveDate(notice.at)} {formatNaiveTime(notice.at)}
          </span>
        ) : null}
      </div>

      <div
        className={cn(
          "text-2xl leading-none font-semibold tracking-tight text-gray-400 tabular-nums",
          strike,
        )}
      >
        {formatNaiveTime(former.departureAt)}
      </div>
      <div className="flex justify-center pt-1">
        <span className="box-border size-2.5 rounded-full border-2 border-red-600 bg-white" aria-hidden />
      </div>
      <div className="flex flex-wrap items-baseline gap-2 leading-tight">
        <span className="font-mono text-base font-bold text-gray-500">{former.from ?? "—"}</span>
        {airportName(former.from, reference) ? (
          <span className="text-sm text-gray-500">{airportName(former.from, reference)}</span>
        ) : null}
      </div>

      <div className="pt-3 text-xs text-gray-400 tabular-nums">
        {duration !== null ? formatDuration(duration) : ""}
      </div>
      <div className="flex justify-center">
        <span className="my-1 min-h-10 w-0 border-l-2 border-dashed border-red-300" aria-hidden />
      </div>
      <div className="flex flex-wrap items-center gap-2.5 py-2.5">
        <Avatar className="size-7 rounded-md border border-gray-200 bg-white">
          {airline?.iconUrl ? (
            <AvatarImage
              className="opacity-50"
              src={airline.iconUrl}
              alt={airline.name ?? former.carrier ?? ""}
            />
          ) : null}
          <AvatarFallback className="rounded-md bg-white text-xs font-bold tracking-wide text-gray-400">
            {former.carrier ?? "—"}
          </AvatarFallback>
        </Avatar>
        <span className="shrink-0 text-sm whitespace-nowrap text-gray-500">
          {airline ? <span className="font-semibold">{airline.name} </span> : null}
          <span className={strike}>{flightLabel(former)}</span>
        </span>
        {reissued.length > 0 ? (
          <>
            <span className="text-gray-300">·</span>
            <Tooltip>
              <TooltipTrigger render={<span className="text-sm text-gray-400" />}>
                Billet{reissued.length > 1 ? "s" : ""}{" "}
                {reissued
                  .map(({ ticket }, index) =>
                    index === 0
                      ? `${ticket.airlineCode ?? ""}-${ticket.number ?? ""}`
                      : (ticket.number ?? "").slice(-3),
                  )
                  .join(" / ")}
                {reissued[0].coupon.number ? ` · coupon ${reissued[0].coupon.number}` : ""} (réémis)
              </TooltipTrigger>
              <TooltipPopup>
                <ul className="flex flex-col gap-0.5">
                  {reissued.map(({ ticket }) => (
                    <li key={ticket.number}>
                      <span className="font-mono">
                        {ticket.airlineCode ?? ""}-{ticket.number ?? ""}
                      </span>
                      {ticket.issuedOn
                        ? ` · émis le ${formatNaiveDate(ticket.issuedOn, { year: true })}`
                        : ""}
                    </li>
                  ))}
                </ul>
              </TooltipPopup>
            </Tooltip>
          </>
        ) : null}
      </div>

      <div
        className={cn(
          "text-2xl leading-none font-semibold tracking-tight text-gray-400 tabular-nums",
          strike,
        )}
      >
        {formatNaiveTime(former.arrivalAt)}
      </div>
      <div className="flex justify-center pt-1">
        <span className="box-border size-2.5 rounded-full border-2 border-red-600 bg-white" aria-hidden />
      </div>
      <div className="flex flex-wrap items-baseline gap-2 leading-tight">
        <span className="font-mono text-base font-bold text-gray-500">{former.to ?? "—"}</span>
        {airportName(former.to, reference) ? (
          <span className="text-sm text-gray-500">{airportName(former.to, reference)}</span>
        ) : null}
        <span className="text-xs text-gray-500">arrivée prévue initialement</span>
      </div>
    </div>
  );
}

/** The solid red line, arrow down, that ties the dropped flight to the one booked in its place. */
function ReroutingConnector({
  former,
  replacement,
  reference,
}: {
  former: PnrDisplaySegment;
  replacement: PnrDisplaySegment;
  reference: PnrReference;
}) {
  const deltas = reroutingDeltas(former, replacement, reference);

  return (
    <div className={cn(GRID, "px-4")}>
      <div />
      <div className="relative flex justify-center">
        <span className="min-h-11 w-0 border-l-2 border-red-600" aria-hidden />
        <ChevronDown className="absolute -bottom-0.5 size-3.5 stroke-[2.5] text-red-600" aria-hidden />
      </div>
      <div className="flex flex-col gap-1 py-3">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
          <Shuffle className="size-3.5 text-red-600" aria-hidden />
          <span className="font-semibold">Réacheminé sur {flightLabel(replacement)}</span>
          <span className="text-gray-600">
            départ {formatNaiveTime(replacement.departureAt)}
            {deltas.departureMinutes !== null ? (
              <>
                , soit{" "}
                <span className="font-semibold text-red-700">{shiftLabel(deltas.departureMinutes)}</span>
              </>
            ) : null}
          </span>
        </div>
        {deltas.arrivalMinutes !== null ? (
          <div className="pl-5.5 text-xs text-gray-600">
            Arrivée {former.to ?? "—"} {deltas.arrivalIsActual ? "réelle" : "prévue"} {deltas.arrivalLocal}{" "}
            contre {formatNaiveTime(former.arrivalAt)} prévue :{" "}
            <span className="font-semibold text-red-700">
              {deltas.arrivalMinutes < 0 ? "avance" : "retard"} de{" "}
              {formatDuration(Math.abs(deltas.arrivalMinutes))}
            </span>{" "}
            à destination finale.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Connector({ children }: { children: React.ReactNode }) {
  return (
    <div className={cn(GRID, "px-4")}>
      <div />
      <div className="flex justify-center">
        <span className="min-h-14 w-0 border-l-2 border-dashed border-gray-300" aria-hidden />
      </div>
      <div className="flex flex-col gap-2 py-3.5">{children}</div>
    </div>
  );
}

function LayoverConnector({
  layover,
  initialMinutes,
  reference,
}: {
  layover: Layover;
  /** The connection as first planned, when the next flight replaced a dropped one. */
  initialMinutes: number | null;
  reference: PnrReference;
}) {
  return (
    <Connector>
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <Clock className="size-3.5 text-gray-500" aria-hidden />
        <span className="font-semibold text-gray-900">
          Escale à {airportCity(layover.airport, reference)}
          {layover.airport ? ` (${layover.airport})` : ""}
        </span>
        {layover.minutes !== null ? (
          <span>
            {initialMinutes !== null && initialMinutes !== layover.minutes ? (
              <span className="mr-1 text-gray-500 line-through decoration-red-600 decoration-2">
                {formatDuration(initialMinutes)}
              </span>
            ) : null}
            <span className="font-semibold text-gray-900">{formatDuration(layover.minutes)}</span>
          </span>
        ) : null}
        {layover.isLong ? <Pill tone="warn">Longue escale</Pill> : null}
        {layover.airportChange ? (
          <Pill tone="indigo">Changement d'aéroport → {layover.nextAirport}</Pill>
        ) : null}
      </div>
    </Connector>
  );
}

// biome-ignore-start lint/style/useNamingConvention: Amadeus codes as keys
const GROUND_ICON: Record<string, typeof Car> = { CCR: Car, HHL: Hotel, TRN: TrainFront };
// biome-ignore-end lint/style/useNamingConvention: Amadeus codes as keys

function GroundRow({ segment }: { segment: PnrDisplaySegment }) {
  const Icon = (segment.type && GROUND_ICON[segment.type]) || Car;
  const label = (segment.type && GROUND_TYPE_LABELS[segment.type]) || segment.type || "Élément";
  const company = segment.carrier ? (CAR_RENTAL_COMPANIES[segment.carrier] ?? null) : null;
  const statusCode = segmentStatusCode(segment);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
      <Icon className="size-3.5 text-gray-500" aria-hidden />
      <span>
        {label}
        {company ? (
          <>
            {" "}
            <span className="font-medium text-gray-900">{company}</span> ({segment.carrier})
          </>
        ) : segment.carrier ? (
          ` ${segment.carrier}`
        ) : (
          ""
        )}
        {segment.from ? ` · ${segment.from}` : ""}
        {segment.departureAt
          ? ` · ${formatNaiveDate(segment.departureAt)} ${formatNaiveTime(segment.departureAt)}`
          : ""}
        {segment.arrivalAt
          ? ` → ${formatNaiveDate(segment.arrivalAt)} ${formatNaiveTime(segment.arrivalAt)}`
          : ""}
      </span>
      {statusCode ? <StatusPill code={statusCode} /> : null}
      <span className="font-mono text-xs text-gray-400">
        {segment.type} · ligne {segment.line}
      </span>
    </div>
  );
}

function StayConnector({ stay, reference }: { stay: Stay; reference: PnrReference }) {
  const openJaw = stay.nextAirport !== stay.airport;

  return (
    <Connector>
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <Hotel className="size-3.5 text-gray-500" aria-hidden />
        <span>
          <span className="font-semibold text-gray-900">
            Séjour à {airportCity(stay.airport, reference)} · {formatDuration(stay.minutes)}
          </span>
          {openJaw ? ` · arrivée ${stay.airport ?? "—"}, reprise depuis ${stay.nextAirport ?? "—"}` : ""}
        </span>
        {openJaw ? <Pill tone="indigo">Open-jaw</Pill> : null}
      </div>
      {stay.ground.map((segment) => (
        <GroundRow key={segment.tattoo ?? segment.line} segment={segment} />
      ))}
    </Connector>
  );
}

function SliceCard({
  journey,
  view,
  reference,
  replacements,
}: {
  journey: Journey;
  view: PnrDisplayView;
  reference: PnrReference;
  replacements: Replacements;
}) {
  const stops = journey.layovers.length;
  const duration = elapsedMinutes(
    journey.departureAt,
    journey.from,
    journey.arrivalAt,
    journey.to,
    reference,
  );
  const single = journey.segments.length === 1;
  const formersOf = (segment: PnrDisplaySegment) =>
    segment.tattoo !== null ? (replacements.replaces.get(segment.tattoo) ?? []) : [];
  const cancelledByMonitoring = single && actualTimes(journey.segments[0], reference)?.cancelled === true;
  // The rerouting the journey suffered, summed up by what matters: the arrival at destination.
  const rerouting = journey.segments
    .flatMap((segment) => formersOf(segment).map((former) => reroutingDeltas(former, segment, reference)))
    .at(-1);

  return (
    <Collapsible defaultOpen className="overflow-hidden rounded-lg border border-gray-200">
      <CollapsibleTrigger className="group flex w-full flex-wrap items-center justify-between gap-3 border-gray-200 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none data-panel-open:border-b">
        <span className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-xs font-bold tracking-wider text-primary uppercase">{journey.label}</span>
          <span className="text-sm font-semibold text-gray-900">
            {formatNaiveDate(journey.departureAt, { weekday: true, year: true })}
          </span>
          <span className="text-sm text-gray-600">
            {journey.from ?? "—"} → {journey.to ?? "—"} ·{" "}
            {stops === 0 ? "direct" : `${stops} escale${stops > 1 ? "s" : ""}`}
            {duration !== null ? ` · ${formatDuration(duration)}` : ""}
          </span>
        </span>
        <span className="flex items-center gap-2">
          {rerouting ? (
            <ReroutingPill>
              Réacheminement
              {rerouting.arrivalMinutes !== null
                ? ` · arrivée ${rerouting.arrivalMinutes >= 0 ? "+" : "−"}${formatDuration(Math.abs(rerouting.arrivalMinutes))}`
                : ""}
            </ReroutingPill>
          ) : null}
          {single ? (
            <FlightStatusPills
              segment={journey.segments[0]}
              view={view}
              cancelledByMonitoring={cancelledByMonitoring}
              replaces={formersOf(journey.segments[0])}
            />
          ) : null}
          <ChevronDown
            className="size-4 text-gray-400 transition-transform group-data-panel-open:rotate-180"
            aria-hidden
          />
        </span>
      </CollapsibleTrigger>

      <CollapsiblePanel>
        {journey.segments.map((segment, index) => {
          const formers = formersOf(segment);
          const next = journey.segments[index + 1];
          const nextFormer = next ? formersOf(next)[0] : undefined;

          return (
            <div key={segment.tattoo ?? segment.line}>
              {/* The dropped flight sits where it was planned, then the red arrow leads to its replacement. */}
              {formers.map((former) => (
                <div key={former.tattoo ?? former.line}>
                  <GhostLeg former={former} replacement={segment} view={view} reference={reference} />
                  <ReroutingConnector former={former} replacement={segment} reference={reference} />
                </div>
              ))}
              <FlightBlock
                segment={segment}
                view={view}
                reference={reference}
                replaces={formers}
                showStatuses={!single}
              />
              {journey.layovers[index] ? (
                <LayoverConnector
                  layover={journey.layovers[index]}
                  initialMinutes={
                    nextFormer ? minutesBetween(segment.arrivalAt, nextFormer.departureAt) : null
                  }
                  reference={reference}
                />
              ) : null}
            </div>
          );
        })}
      </CollapsiblePanel>
    </Collapsible>
  );
}

function OtherElement({ segment }: { segment: PnrDisplaySegment }) {
  const label = segment.type ? (ELEMENT_TYPE_LABELS[segment.type] ?? segment.type) : "Élément";
  const statusCode = segmentStatusCode(segment);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Hors itinéraire</span>
        <span>
          <span className="font-medium text-gray-900">
            {label}
            {segment.type && label !== segment.type ? ` (${segment.type})` : ""}
          </span>
          {segment.carrier ? ` · ${segment.carrier}` : ""}
          {segment.from ? ` · ${segment.from}` : ""}
          {segment.departureAt ? ` · ${formatNaiveDate(segment.departureAt, { year: true })}` : ""}
        </span>
        {statusCode ? <StatusPill code={statusCode} /> : null}
      </div>
      <span className="font-mono text-xs text-gray-400">ligne {segment.line}</span>
    </div>
  );
}

/**
 * The itinerary as the PNR image records it, read the Duffel way: one card per journey with a
 * vertical timeline (clock → airport), the flight in the middle, stays and ground segments as
 * connectors, and whatever is not a trip filed at the bottom. A flight the airline dropped stays
 * on the timeline as a ghost leg, tied by a red arrow to the flight booked in its place; actual
 * times come from flight monitoring or from the airline's own FDL lines in the PNR.
 */
export function PnrItinerary({
  view,
  reference = EMPTY_REFERENCE,
}: {
  view: PnrDisplayView;
  reference?: PnrReference;
}) {
  const groups = groupItinerary(view.itinerary);
  const replacements = linkReplacements(groups, view);
  const orphans = groups.cancelled.filter(
    (segment) => !segment.tattoo || !replacements.replacedBy.has(segment.tattoo),
  );
  // Trips and unreplaced cancelled flights interleaved by date, labelled as one trip.
  const { tripType, items } = sequenceItinerary(groups, orphans);
  const legs = items.map((item) => (item.kind === "journey" ? item.journey : item.segment));
  const first = legs[0];
  const last = legs[legs.length - 1];
  const RouteIcon = tripType === "Aller-retour" ? ArrowRightLeft : ArrowRight;
  const destination = tripType === "Aller-retour" ? first?.to : last?.to;

  return (
    <div className="rounded-lg border border-gray-200 px-6 py-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold tracking-wide text-gray-600 uppercase">Itinéraire</span>
        {first ? (
          <span className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{tripType}</span>
            <span className="text-gray-400">·</span>
            <span>{airportCity(first.from, reference)}</span>
            <RouteIcon className="size-3.5 text-gray-500" aria-hidden />
            <span>{airportCity(destination ?? null, reference)}</span>
            <span className="text-gray-400">·</span>
            <span>{formatDateRange(first.departureAt, last?.arrivalAt ?? null)}</span>
          </span>
        ) : null}
      </div>

      <div className="mt-5 flex flex-col">
        {groups.journeys.length === 0 ? <p className="text-sm text-gray-500">Aucun vol dans ce PNR</p> : null}

        {items.map((item, index) =>
          item.kind === "journey" ? (
            <div key={`${item.journey.from}-${item.journey.departureAt}`}>
              <SliceCard
                journey={item.journey}
                view={view}
                reference={reference}
                replacements={replacements}
              />
              {item.stay ? <StayConnector stay={item.stay} reference={reference} /> : null}
            </div>
          ) : (
            // A dropped flight nothing replaced keeps its place in the trip: the ghost leg alone.
            <div
              key={item.segment.tattoo ?? item.segment.line}
              className={cn(
                "overflow-hidden rounded-lg border border-red-200",
                index > 0 && "mt-4",
                index < items.length - 1 && "mb-4",
              )}
            >
              <div className="flex flex-wrap items-baseline gap-2.5 border-b border-red-200 bg-red-50 px-4 py-2.5">
                <span className="text-xs font-bold tracking-wider text-red-700 uppercase">{item.label}</span>
                <span className="text-sm text-gray-600">
                  {item.segment.from ?? "—"} → {item.segment.to ?? "—"} · annulé, non remplacé
                </span>
              </div>
              <GhostLeg former={item.segment} replacement={null} view={view} reference={reference} />
            </div>
          ),
        )}

        {groups.others.length > 0 ? (
          <div className="mt-4 flex flex-col gap-2">
            {groups.others.map((segment) => (
              <OtherElement key={segment.tattoo ?? segment.line} segment={segment} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
